import React, { useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import { metaStyle as meta } from '../utils/shopHealth';

// ONE FEED'S PROCESSING RUNS, on that feed's own room.
//
// The log existed only as a whole-account list inside `/feeds`'s `Advanced`
// disclosure — every feed's runs interleaved, which answers "did anything run?"
// and never "did THIS one?". It could not be filtered because
// `feed_processing_history` is a view over `feed_uploads` that carried no
// reference to the feed at all; migration 070 adds `feed_ref` to it.
//
// It loads only when asked. A publisher opening a room wants the shop's state
// and its buttons; the run log is evidence for when something looks wrong, so it
// costs nothing until it is opened.

interface Props {
  feedId: number;
  label: string;
}

interface Run {
  feed_ref?: number | null;
  filename: string;
  total_products: number;
  processed_products: number;
  skipped_products: number;
  created_at: string;
  success_rate?: number;
}

const when = (iso: string) => {
  const h = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 3_600_000));
  if (h === 0) return 'just now';
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

// A run whose filename starts `sync-feed-` came from the scheduler; anything
// else was a file the publisher sent. Same rule `FeedList` uses, and it is a
// filename convention rather than a column — worth knowing before trusting it.
const source = (filename: string) => (filename.startsWith('sync-feed-') ? 'Sync' : 'Upload');

const FeedRunHistory: React.FC<Props> = ({ feedId, label }) => {
  const [open, setOpen] = useState(false);
  const [runs, setRuns] = useState<Run[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open || runs !== null) return;
    apiService.getFeedHistory({ feedRef: feedId, limit: 10 })
      .then(r => setRuns(((r as any).history ?? []) as Run[]))
      .catch(() => setFailed(true));
  }, [open, runs, feedId]);

  return (
    <details
      data-testid={`feed-runs-${feedId}`}
      style={{ ...meta, marginTop: '1rem' }}
      onToggle={e => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary style={{ cursor: 'pointer' }}>Processing runs</summary>

      {failed && (
        <p role="alert" data-testid={`feed-runs-error-${feedId}`} style={{ color: 'var(--danger)' }}>
          We could not load this feed’s runs just now.
        </p>
      )}

      {!failed && runs === null && <p style={{ marginTop: '0.5rem' }}>Loading…</p>}

      {/* An empty log is a real answer for a feed that has never run — a shop
          added minutes ago, or one whose first sync has not landed. */}
      {!failed && runs !== null && runs.length === 0 && (
        <p data-testid={`feed-runs-empty-${feedId}`} style={{ marginTop: '0.5rem' }}>
          Nothing has processed for {label} yet.
        </p>
      )}

      {!failed && runs !== null && runs.length > 0 && (
        <table className="data-table" style={{ marginTop: '0.5rem' }}>
          <thead>
            <tr>
              <th>When</th>
              <th>Source</th>
              <th className="num">Products</th>
              <th className="num">Skipped</th>
            </tr>
          </thead>
          <tbody>
            {runs.map(r => (
              <tr key={`${r.filename}-${r.created_at}`} data-testid={`feed-run-${r.created_at}`}>
                <td>{when(r.created_at)}</td>
                <td>{source(r.filename)}</td>
                <td className="num">{r.processed_products.toLocaleString()}</td>
                {/* SKIPPED IS TWO THINGS SUMMED, AND THEY MEAN OPPOSITE THINGS.
                    The server writes `rejectedRows + duplicateRows` into one
                    column (`services/affiliate-feeds.ts`): a rejected row is a
                    product lost, a duplicate is the SAME product arriving twice
                    and being collapsed onto one id — nothing lost at all.

                    Measured on dyfashion 2026-09-07 by replaying the live export
                    through the real parser: of 929 skipped, 0 were rejects and
                    929 were duplicates. Its 2Performant export unions several of
                    the advertiser's category sub-feeds, so a product in two
                    categories arrives twice, identical but for the `unique`
                    tracking param — same destination, same commission. The run
                    before that union was 1,272/1,272 at 100%; adding it took the
                    catalogue to 2,118 distinct products AND created the skip
                    count. So a large number here is usually a wider catalogue,
                    not a broken one.

                    The split is not persisted, so this column cannot tell them
                    apart yet. Until it can, read a big number as a question
                    rather than an alarm. Zero is left blank so the ones that are
                    not zero are what the eye lands on. */}
                <td className="num">{r.skipped_products > 0 ? r.skipped_products.toLocaleString() : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </details>
  );
};

export default FeedRunHistory;
